/**
 * Ranking API — Supabase
 */

import { supabase, TABLES } from './db';
import { RANK_TIERS, BADGE_DEFS, SCORE_RULES, RankTier, BadgeDef } from '../constants/ranks';

export interface UserRankData {
  score: number;
  rank: RankTier;
  nextRank: RankTier | null;
  progressToNext: number;
  taskCount: number;
  promptCount: number;
  completedTaskCount: number;
  earnedBadges: BadgeDef[];
  weeklyTaskPeak: number;
  weeklyPromptPeak: number;
}

function getIsoWeekKey(ms: number): number {
  return Math.floor(ms / (7 * 24 * 60 * 60 * 1000));
}

function tsToMs(val: unknown): number {
  if (!val) return 0;
  if (typeof val === 'string') return new Date(val).getTime();
  if (typeof val === 'number') return val;
  return 0;
}

export async function computeUserRank(userId: string): Promise<UserRankData> {
  const [taskSnap, promptSnap] = await Promise.all([
    supabase.from(TABLES.TASK_HATCH_SUBMISSIONS).select('status, created_at').eq('user_id', userId),
    supabase.from(TABLES.PROMPT_POSTS).select('created_at').eq('author_id', userId),
  ]);

  const tasks = (taskSnap.data ?? []) as Array<{ status?: string; created_at?: string }>;
  const prompts = (promptSnap.data ?? []) as Array<{ created_at?: string }>;

  const taskCount = tasks.length;
  const promptCount = prompts.length;
  const completedTaskCount = tasks.filter((t) => t.status === 'completed').length;

  const tasksByWeek = new Map<number, number>();
  for (const task of tasks) {
    const ms = tsToMs(task.created_at);
    if (ms > 0) {
      const wk = getIsoWeekKey(ms);
      tasksByWeek.set(wk, (tasksByWeek.get(wk) || 0) + 1);
    }
  }

  const promptsByWeek = new Map<number, number>();
  for (const prompt of prompts) {
    const ms = tsToMs(prompt.created_at);
    if (ms > 0) {
      const wk = getIsoWeekKey(ms);
      promptsByWeek.set(wk, (promptsByWeek.get(wk) || 0) + 1);
    }
  }

  const taskWeekValues = Array.from(tasksByWeek.values());
  const promptWeekValues = Array.from(promptsByWeek.values());
  const weeklyTaskPeak = taskWeekValues.length > 0 ? Math.max(...taskWeekValues) : 0;
  const weeklyPromptPeak = promptWeekValues.length > 0 ? Math.max(...promptWeekValues) : 0;

  const sprintBonusCount = taskWeekValues.filter((c) => c >= 5).length;
  const promptStormBonusCount = promptWeekValues.filter((c) => c >= 20).length;

  const score =
    taskCount * SCORE_RULES.taskCreated +
    promptCount * SCORE_RULES.promptPosted +
    completedTaskCount * SCORE_RULES.taskCompleted +
    sprintBonusCount * SCORE_RULES.sprintBonus +
    promptStormBonusCount * SCORE_RULES.promptStormBonus;

  let rank = RANK_TIERS[0];
  for (const tier of RANK_TIERS) {
    if (score >= tier.minScore) rank = tier;
  }

  const rankIndex = RANK_TIERS.findIndex((t) => t.id === rank.id);
  const nextRank = rankIndex < RANK_TIERS.length - 1 ? RANK_TIERS[rankIndex + 1] : null;

  let progressToNext = 100;
  if (nextRank) {
    const rangeSize = nextRank.minScore - rank.minScore;
    const progress = score - rank.minScore;
    progressToNext = Math.min(100, Math.round((progress / rangeSize) * 100));
  }

  const earnedBadges: BadgeDef[] = [];
  const promptOnlyScore = promptCount * SCORE_RULES.promptPosted + promptStormBonusCount * SCORE_RULES.promptStormBonus;

  const allWeekKeys = Array.from(
    new Set([...Array.from(tasksByWeek.keys()), ...Array.from(promptsByWeek.keys())])
  ).sort((a, b) => a - b);

  let maxConsecutive = 0;
  let currentStreak = 0;
  for (let i = 0; i < allWeekKeys.length; i++) {
    if (i === 0 || allWeekKeys[i] === allWeekKeys[i - 1] + 1) {
      currentStreak++;
      maxConsecutive = Math.max(maxConsecutive, currentStreak);
    } else {
      currentStreak = 1;
    }
  }

  const addBadge = (id: string) => { const b = BADGE_DEFS.find((b) => b.id === id); if (b) earnedBadges.push(b); };
  if (weeklyTaskPeak >= 5) addBadge('sprint_creator');
  if (weeklyPromptPeak >= 20) addBadge('prompt_storm');
  if (taskCount >= 100) addBadge('task_centurion');
  if (taskCount >= 500) addBadge('task_master');
  if (promptCount >= 50) addBadge('voice_grid');
  if (promptCount >= 200) addBadge('prompt_sage');
  if (taskCount >= 10) addBadge('global_node');
  if (promptOnlyScore >= 1000) addBadge('ai_pioneer');
  if (maxConsecutive >= 4) addBadge('weekly_streak');

  return { score, rank, nextRank, progressToNext, taskCount, promptCount, completedTaskCount, earnedBadges, weeklyTaskPeak, weeklyPromptPeak };
}
